// Purchasing tab orchestrator (F-FE-N97). The tab that used to be a 1,300-line
// status table with a "How purchasing works" walkthrough is now a queue plus
// drill-ins: PurchasingQueue (what needs doing), PoDetailSheet (one order,
// URL-addressable), and the create / send / receive / return / print dialogs.
// This file only wires them together: URL state, which dialog is open, and the
// one mutation shared by row and sheet (approve).
import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import apiClient from '@/api/client'
import type { Supplier, PurchaseOrderSummary, PurchaseOrderDetail, StockItem, StockAlert } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { getStoredUserId, getStoredUserRole } from '@/lib/staff-roles'
import { parseReceiptSettings } from '@/lib/printCustomerReceipt'
import { PurchasingQueue } from './PurchasingQueue'
import { PoDetailSheet } from './PoDetailSheet'
import { PoCreateDialog, type PODraft } from './PoCreateDialog'
import { PoSendDialog } from './PoSendDialog'
import { PoReceiveDialog } from './PoReceiveDialog'
import { PoReturnDialog } from './PoReturnDialog'
import { PoPrintModal } from './PoPrintModal'
import type { InventorySearch } from './inventoryRouteSearch'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { KitchenFlowPurchasingBar } from './purchasing/KitchenFlowPurchasingBar'
import { canApprovePO } from './purchasing/purchasingLogic'
import { canApproveThisPO, parseSelfApproval } from './purchasing/purchasingPilotLogic'
import { AlreadyBoughtDialog, NewPurchaseChoiceDialog } from './purchasing/AlreadyBoughtDialog'
import { StockCashOutsBanner } from './purchasing/StockCashOutsBanner'
import { marketPlacesFor } from '@/lib/inventoryFlow/marketLogic'
import { BT } from '@/lib/inventoryFlow/boughtStrings'

type ToastFn = (type: 'success' | 'error', message: string) => void

export type { PODraft }

const PAGE_SIZE = 100

export function InventoryPurchasingTab({ stockItems, alerts, showToast, initialDraft, onDraftConsumed }: {
  stockItems: StockItem[]
  alerts: StockAlert[]
  showToast: ToastFn
  initialDraft?: PODraft | null
  onDraftConsumed?: () => void
}) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as InventorySearch
  const { formatCurrency } = useCurrency()
  const isAdmin = getStoredUserRole() === 'admin'

  // ── Data ─────────────────────────────────────────────────────────────────
  const { data: supRes } = useQuery({ queryKey: ['suppliers'], queryFn: () => apiClient.listSuppliers() })
  const suppliers: Supplier[] = supRes?.data ?? []

  const [perPage, setPerPage] = useState(PAGE_SIZE)
  const { data: poRes, isPending: ordersLoading, isError: ordersError, refetch: refetchOrders } = useQuery({
    queryKey: ['purchaseOrders', { page: 1, per_page: perPage }],
    queryFn: () => apiClient.listPurchaseOrders({ page: 1, per_page: perPage }),
  })
  const orders: PurchaseOrderSummary[] = poRes?.data ?? []
  const hasMore = (poRes?.meta?.total ?? 0) > orders.length

  const { data: settingsRes } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 1000 * 60 * 5,
    enabled: apiClient.isAuthenticated(),
    retry: false,
  })
  // Kitchen flow lane D: po_approvers may let the inventory manager approve.
  // Flag off → exactly isAdmin, as before.
  const kitchenFlow = useKitchenFlowEnabled()
  const canApprove = canApprovePO(
    getStoredUserRole(),
    (settingsRes?.data as Record<string, unknown> | undefined)?.po_approvers,
    kitchenFlow,
  )

  // Kitchen flow pilot (Batch 5): with "the person who makes an order may
  // also approve it" off, Approve is hidden on your own order (the server
  // refuses it too). Flag off → exactly canApprove.
  const selfApproval = parseSelfApproval((settingsRes?.data as Record<string, unknown> | undefined)?.po_self_approval)
  const canApproveOrder = (o: { created_by?: string }) =>
    kitchenFlow
      ? canApproveThisPO({ canApprove, role: getStoredUserRole(), createdBy: o.created_by, myId: getStoredUserId(), selfApproval })
      : canApprove

  const receiptSettings = parseReceiptSettings(
    settingsRes?.success && settingsRes.data ? (settingsRes.data as Record<string, unknown>) : undefined,
  )

  // ── Which drill-in / dialog is open ──────────────────────────────────────
  const activePoId = search.po ?? null
  const openPo = (id: string) =>
    navigate({ to: '/admin/inventory', search: { tab: 'purchasing', po: id }, replace: true })
  const closePo = () =>
    navigate({ to: '/admin/inventory', search: { tab: 'purchasing' }, replace: true })

  const [createOpen, setCreateOpen] = useState(false)
  // F-CTRL-N75: New purchase = order from a supplier, or already bought.
  const canRecordBought = kitchenFlow && marketPlacesFor(getStoredUserRole()).length > 0
  const [choiceOpen, setChoiceOpen] = useState(false)
  const [boughtOpen, setBoughtOpen] = useState(false)
  const [createDraft, setCreateDraft] = useState<PODraft | null>(null)
  const [receivePoId, setReceivePoId] = useState<string | null>(null)
  const [returnPoId, setReturnPoId] = useState<string | null>(null)
  const [sendDetail, setSendDetail] = useState<PurchaseOrderDetail | null>(null)
  const [printDetail, setPrintDetail] = useState<PurchaseOrderDetail | null>(null)

  // Alerts-tab handoff: "Create PO for <vendor>" arrives as a pre-filled
  // draft. One-shot — the parent clears it once consumed.
  useEffect(() => {
    if (!initialDraft || initialDraft.lines.length === 0) return
    setCreateDraft(initialDraft)
    setCreateOpen(true)
    onDraftConsumed?.()
  }, [initialDraft, onDraftConsumed])

  // ?orderFor=<vendor> (the vendor profile's "New order" button): open the
  // create dialog seeded for that vendor, then drop the param from the URL.
  useEffect(() => {
    if (!search.orderFor) return
    setCreateDraft({ supplier_id: search.orderFor, lines: [] })
    setCreateOpen(true)
    navigate({ to: '/admin/inventory', search: { tab: 'purchasing' }, replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.orderFor])

  // ── Shared actions ───────────────────────────────────────────────────────
  const approvePO = useMutation({
    mutationFn: (id: string) => apiClient.approvePurchaseOrder(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      qc.invalidateQueries({ queryKey: ['purchaseOrder'] })
      showToast('success', 'Order approved — it can be sent now')
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to approve'),
  })

  const loadDetail = async (id: string): Promise<PurchaseOrderDetail | null> => {
    try {
      const r = await apiClient.getPurchaseOrder(id)
      if (r.success && r.data) return r.data
      showToast('error', r.message || 'Could not load the order')
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Could not load the order')
    }
    return null
  }

  const onPrimaryAction = async (o: PurchaseOrderSummary, action: 'approve' | 'send' | 'receive') => {
    if (action === 'approve') approvePO.mutate(o.id)
    else if (action === 'receive') setReceivePoId(o.id)
    else {
      const d = await loadDetail(o.id)
      if (d) setSendDetail(d)
    }
  }

  const onCreated = async (id: string) => {
    setCreateDraft(null)
    if (!isAdmin) return
    // An admin's order is born ready-to-send — offer to send it right away.
    const d = await loadDetail(id)
    if (d) setSendDetail(d)
  }

  const openNewOrder = () => {
    setCreateDraft(null)
    if (canRecordBought) setChoiceOpen(true)
    else setCreateOpen(true)
  }

  // The Running-low tile: open the create dialog in suggestion mode (no vendor
  // picked — it lists the vendors with low items as starting points).
  const openOrderLow = () => {
    setCreateDraft(null)
    setCreateOpen(true)
  }

  const businessName = receiptSettings.businessName
  const printFormat = useMemo(() => formatCurrency, [formatCurrency])

  return (
    <>
      <KitchenFlowPurchasingBar onOpenPo={openPo} showToast={showToast} />
      {kitchenFlow && <StockCashOutsBanner />}
      <PurchasingQueue
        orders={orders}
        suppliers={suppliers}
        alerts={alerts}
        isAdmin={isAdmin}
        canApprove={canApprove}
        canApproveOrder={canApproveOrder}
        loading={ordersLoading}
        loadError={ordersError}
        onRetry={() => void refetchOrders()}
        hasMore={hasMore}
        onLoadMore={() => setPerPage((p) => p + PAGE_SIZE)}
        onOpenPo={openPo}
        onNewOrder={openNewOrder}
        onOrderLow={openOrderLow}
        onPrimaryAction={onPrimaryAction}
        newLabel={canRecordBought ? BT.newPurchase : undefined}
        newNeedsVendor={!canRecordBought}
      />

      <NewPurchaseChoiceDialog
        open={choiceOpen}
        onOpenChange={setChoiceOpen}
        onOrder={() => setCreateOpen(true)}
        onBought={() => setBoughtOpen(true)}
      />
      <AlreadyBoughtDialog
        open={boughtOpen}
        onOpenChange={setBoughtOpen}
        suppliers={suppliers}
        showToast={showToast}
        onRecorded={openPo}
      />

      <PoDetailSheet
        poId={activePoId}
        onClose={closePo}
        isAdmin={isAdmin}
        canApprove={canApprove}
        canApproveOrder={canApproveOrder}
        showToast={showToast}
        onReceive={setReceivePoId}
        onReturn={setReturnPoId}
        onSend={setSendDetail}
        onApprove={(id) => approvePO.mutate(id)}
        onPrint={(d) => setPrintDetail(d)}
      />

      <PoCreateDialog
        open={createOpen}
        onOpenChange={(o) => {
          setCreateOpen(o)
          if (!o) setCreateDraft(null)
        }}
        suppliers={suppliers}
        stockItems={stockItems}
        alerts={alerts}
        initialDraft={createDraft}
        isAdmin={isAdmin}
        showToast={showToast}
        onCreated={onCreated}
      />

      <PoSendDialog
        detail={sendDetail}
        onClose={() => setSendDetail(null)}
        showToast={showToast}
        onPrint={(d) => setPrintDetail(d)}
      />

      <PoReceiveDialog
        poId={receivePoId}
        onClose={() => setReceivePoId(null)}
        isAdmin={isAdmin}
        showToast={showToast}
      />

      <PoReturnDialog poId={returnPoId} onClose={() => setReturnPoId(null)} showToast={showToast} />

      <PoPrintModal
        open={!!printDetail}
        onOpenChange={(o) => !o && setPrintDetail(null)}
        detail={printDetail}
        businessName={businessName}
        formatCurrency={printFormat}
        receiptSettings={receiptSettings}
      />
    </>
  )
}
